import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Combobox,
  Transition,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  CheckIcon,
  XIcon,
  RefreshCcwIcon,
} from "lucide-react";
import React, { Fragment, useState, useEffect } from "react";
import { createUser, updateCognitoEmailTemplate } from "../../../../service/apiService";
import FilePermissions from "./FilePermissions";
import { NewUser } from "../../../../types";

const roles = ["admin", "user"];

interface CreateUserProps {
  onUserChange: () => void;
}

const CreateUser: React.FC<CreateUserProps> = ({ onUserChange }) => {
  const [newUser, setNewUser] = useState<NewUser>({ 
    user_id: "", 
    role: "", 
    user_status: "enabled",
    permissions: {
      files: {
        canDownload: true,
        canPreview: true,
      },
    },
  });
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [query, setQuery] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const filteredRoles =
    query === ""
      ? roles
      : roles.filter((role) =>
          role.toLowerCase().includes(query.toLowerCase())
        );

  useEffect(() => {
    validateForm();
  }, [newUser, selectedRole]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setNewUser((prevUser) => ({ ...prevUser, [name]: value }));
  };

  const handleRoleChange = (role: string) => {
    setSelectedRole(role);
    handleInputChange({
      target: { name: "role", value: role } as any,
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const validateForm = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (newUser.user_id && !emailRegex.test(newUser.user_id)) {
      setValidationError("Please enter a valid email address.");
      return false;
    }
    if (newUser.user_id && selectedRole === "") {
      setValidationError("Please select a role.");
      return false;
    }
    setValidationError(null);
    return true;
  };

  const updateEmailTemplate = async () => {
    try {
      await updateCognitoEmailTemplate();
      // Template update happens silently - no user feedback
    } catch (error: any) {
      // Template update error is logged but not shown to user
      console.warn("Template update failed (non-critical):", error);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitSuccess(null);

    try {
      // Step 1: Update template first (silent)
      await updateEmailTemplate();
      
      // Step 2: Create user (will use the updated template)
      await createUser(newUser.user_id, newUser.role, newUser.permissions);
      setSubmitSuccess(true);
      
      // Step 3: Reset form
      setNewUser({ 
        user_id: "", 
        role: "", 
        user_status: "enabled",
        permissions: {
          files: {
            canDownload: true,
            canPreview: true,
          },
        },
      });
      setSelectedRole("");
      onUserChange();
    } catch (error) {
      console.error("Error in user creation process:", error);
      setSubmitSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Disclosure as="div" className="py-2">
      <DisclosureButton className="group flex w-full items-center justify-between">
        <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
          Create User
        </span>
        <ChevronDownIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-300 group-open:rotate-180" />
      </DisclosureButton>
      <DisclosurePanel className="mt-2 text-sm mx-2 p-4 rounded-lg bg-white">
        <form onSubmit={handleFormSubmit} className="space-y-6">
          <div className="flex flex-col">
            <label
              htmlFor="user_id"
              className="block text-sm font-medium text-gray-700"
            >
              User ID
            </label>
            <input
              type="text"
              id="user_id"
              name="user_id"
              placeholder="Email address"
              value={newUser.user_id}
              onChange={handleInputChange}
              className="mt-2 w-full h-12 px-4 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg transition-all duration-200 focus:border-violet-500 focus:outline-none focus:ring focus:ring-violet-200 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-600 dark:focus:border-violet-500 dark:placeholder-gray-400 focus:ring-opacity-20"
              required
            />
            {validationError &&
              newUser.user_id &&
              validationError.includes("email") && (
                <span className="text-red-500 text-sm">{validationError}</span>
              )}
          </div>
          
          <div className="flex flex-col">
            <label
              htmlFor="role"
              className="block text-sm font-medium text-gray-700"
            >
              Role
            </label>
            <Combobox value={selectedRole} onChange={handleRoleChange}>
              <div className="relative mt-1">
                <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-200 focus-visible:ring-offset-2 sm:text-sm h-12">
                  <Combobox.Input
                    className="w-full h-full border border-gray-200 py-2 pl-3 pr-10 text-sm leading-5 text-gray-700 bg-white rounded-lg focus:outline-none focus:border-violet-500 focus:ring focus:ring-violet-200 focus:ring-opacity-20"
                    displayValue={(role: string) => role}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDownIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </Combobox.Button>
                </div>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterLeave={() => setQuery("")}
                >
                  <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                    {filteredRoles.length === 0 && query !== "" ? (
                      <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                        Nothing found.
                      </div>
                    ) : (
                      filteredRoles.map((role) => (
                        <Combobox.Option
                          key={role}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 ${
                              active
                                ? "bg-violet-600 text-white"
                                : "text-gray-900"
                            }`
                          }
                          value={role}
                        >
                          {({ selected, active }) => (
                            <>
                              <span
                                className={`block truncate ${
                                  selected ? "font-medium" : "font-normal"
                                }`}
                              >
                                {role}
                              </span>
                              {selected ? (
                                <span
                                  className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                    active ? "text-white" : "text-violet-600"
                                  }`}
                                >
                                  <CheckIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Combobox.Option>
                      ))
                    )}
                  </Combobox.Options>
                </Transition>
              </div>
            </Combobox>
            {validationError &&
              newUser.user_id &&
              validationError.includes("role") && (
                <span className="text-red-500 text-sm">{validationError}</span>
              )}
          </div>

          {newUser.user_id && newUser.role && (
            <div className="mt-4">
              <FilePermissions
                user={{
                  id: newUser.user_id,
                  role: newUser.role,
                  user_status: newUser.user_status,
                  assigned_knowledge_base: [],
                  updated_at: "",
                  created_at: "",
                  last_login: "",
                  shared_hats: {},
                  short_term_memory_enabled: false,
                  long_term_memory_enabled: false,
                  permissions: newUser.permissions
                }}
                onPermissionsChange={(permissions) =>
                  setNewUser({ ...newUser, permissions })
                }
                disabled={isSubmitting}
              />
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row sm:items-center">
            <button
              type="submit"
              className={`inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                isSubmitting || validationError !== null
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-violet-600 hover:bg-violet-700"
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500`}
              disabled={isSubmitting || validationError !== null}
            >
              {isSubmitting ? (
                <>
                  <RefreshCcwIcon
                    className="animate-spin mr-2 h-5 w-5 text-white"
                    aria-hidden="true"
                  />
                  Creating...
                </>
              ) : (
                "Create User"
              )}
            </button>
            
            {submitSuccess !== null && (
  <div className="mt-2 sm:mt-0 sm:ml-2 flex items-center">
    {submitSuccess ? (
      <>
        <CheckIcon className="h-6 w-6 text-green-500" />
        <span className="ml-2 text-sm text-green-500">
          {newUser.user_id || "User"} successfully created
        </span>
      </>
    ) : (
      <>
        <XIcon className="h-6 w-6 text-red-500" />
        <span className="ml-2 text-sm text-red-500">
          {newUser.user_id || "User"} failed to create
        </span>
      </>
    )}
  </div>
)}
          </div>
        </form>
      </DisclosurePanel>
    </Disclosure>
  );
};

export default CreateUser;